import React from 'react';
import { ArrowRight, Clock } from 'lucide-react';
import type { CadeiaAndonLevelStat, CadeiaAndonResponsibility } from '../../services/cadeiaChainInsights';
import { ANDON_ESCALATION_LEVELS } from '../../services/andonEscalation';
import type { LstCargo } from '../../types';
import { LST_CARGO_LABELS } from '../../types';

interface CadeiaEscalationTimelineProps {
  viewerCargo: LstCargo;
  levels: CadeiaAndonLevelStat[];
  viewerLevel: 1 | 2 | 3;
  chainHasIssue: boolean;
}

const RESPONSIBILITY_STYLE: Record<
  CadeiaAndonResponsibility,
  { border: string; bg: string; badge: string }
> = {
  agir_agora: {
    border: 'border-red-400 ring-1 ring-red-400/40 dark:border-red-700',
    bg: 'bg-red-50/70 dark:bg-red-950/25',
    badge: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  },
  proximo_nivel: {
    border: 'border-orange-300 dark:border-orange-900',
    bg: 'bg-orange-50/50 dark:bg-orange-950/15',
    badge: 'bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300',
  },
  escalonado: {
    border: 'border-violet-300 dark:border-violet-900',
    bg: 'bg-violet-50/50 dark:bg-violet-950/15',
    badge: 'bg-violet-100 text-violet-800 dark:bg-violet-950/40 dark:text-violet-300',
  },
  monitorando: {
    border: 'border-slate-200 dark:border-slate-700',
    bg: 'bg-white dark:bg-slate-900/30',
    badge: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  },
};

/** Fluxo Andon — responsabilidade por nível (não apenas cor). */
export const CadeiaEscalationTimeline: React.FC<CadeiaEscalationTimelineProps> = ({
  viewerCargo,
  levels,
  viewerLevel,
  chainHasIssue,
}) => {
  const metaByLevel = new Map(ANDON_ESCALATION_LEVELS.map((l) => [l.level, l]));

  return (
    <section className="shrink-0 rounded-lg border border-slate-200 bg-white p-2 dark:border-slate-800 dark:bg-slate-950/40">
      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300">
        Fluxo Andon · {LST_CARGO_LABELS[viewerCargo]} (N{viewerLevel})
        {chainHasIssue && (
          <span className="ml-2 font-semibold normal-case text-amber-600 dark:text-amber-400">
            · cadeia com pendências
          </span>
        )}
      </p>

      <div className="flex flex-col gap-1 sm:flex-row sm:items-stretch">
        {levels.map((stat, idx) => {
          const meta = metaByLevel.get(stat.level)!;
          const style = RESPONSIBILITY_STYLE[stat.responsibility];
          const isViewerLevel = stat.level === viewerLevel;

          return (
            <React.Fragment key={stat.level}>
              {idx > 0 && (
                <div className="hidden shrink-0 items-center justify-center sm:flex">
                  <ArrowRight size={14} className="text-slate-300 dark:text-slate-600" />
                </div>
              )}
              <div
                className={`flex min-w-0 flex-1 flex-col rounded-md border px-2 py-1.5 ${style.border} ${style.bg} ${isViewerLevel && chainHasIssue ? 'ring-1 ring-[#2F6FED]/30' : ''}`}
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="text-[10px] font-bold text-slate-800 dark:text-slate-100">
                    N{stat.level} · {stat.shortLabel}
                  </span>
                  <span className={`rounded px-1 py-0.5 text-[8px] font-bold uppercase ${style.badge}`}>
                    {stat.responsibilityLabel}
                  </span>
                </div>
                <p className="mt-0.5 text-[9px] font-medium text-slate-600 dark:text-slate-400">
                  {stat.statusDetail}
                </p>
                <p className="mt-0.5 flex items-center gap-0.5 text-[8px] text-slate-400">
                  <Clock size={8} className="text-amber-500" />
                  SLA {meta.toleranceMinutes} min
                </p>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
};
